import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import * as LucideIcons from "lucide-react";
import { BRAND, BRAND_LINK, API } from 'utils/Helper';

const ChevronDown = LucideIcons.ChevronDown;

const StyledFooter = styled.footer`
  margin-top: 32px;
  background-color: #1B1D21;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding: 32px 16px;
  color: #8E969F;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  
  @media (min-width: 768px) {
    margin-top: 64px;
    padding: 48px 32px;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const FooterGrid = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 40px 24px;
    padding-bottom: 48px;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

const ColumnTitle = styled.h4`
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 16px;
  text-transform: capitalize;
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLink = styled(Link)`
  color: #8E969F;
  text-decoration: none;
  font-size: 13.5px;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: #ffffff;
    text-decoration: none;
  }
`;

const FooterExternalLink = styled.a`
  color: #8E969F;
  text-decoration: none;
  font-size: 13.5px;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: #ffffff;
    text-decoration: none;
  }
`;

const MobileAccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 24px;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const AccordionItem = styled.div`
  background: #22252a;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.03);
`;

const AccordionTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  outline: none;
  
  span {
    font-weight: 700;
    font-size: 14px;
    color: #ffffff;
  }
  
  svg {
    color: #8E969F;
    transition: transform 0.2s ease;
  }
`;

const AccordionContent = styled.div`
  padding: 0 20px 16px 20px;
`;

const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  color: #8E969F;
  padding: 24px 0;
  justify-content: center;
  
  @media (min-width: 768px) {
    justify-content: flex-start;
  }
  
  svg {
    cursor: pointer;
    transition: color 0.2s ease;
    
    &:hover {
      color: #ffffff;
    }
  }
`;

const BottomSection = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-size: 13.5px;
  line-height: 1.6;
  color: #8E969F;
  padding-bottom: 40px;
  
  p {
    margin: 0;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  
  img {
    height: 40px;
    width: auto;
  }
  
  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`;

const LanguageButton = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 8px;
  padding: 0 20px;
  height: 40px;
  border-radius: 6px;
  background: #22252a;
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #2a2e34;
  }
`;

import { useActiveGroups } from 'hooks/useGameEngine';
import { useSiteSettingsQuery } from 'hooks/useAuth';

const colsBase = [
  {
    title: "Support",
    links: [
      { label: "Help Desk", to: "/provably-fair" },
      { label: "Responsible Gambling", to: "/provably-fair" },
      { label: "Self Exclusion", to: "/provably-fair" }
    ]
  },
  {
    title: "About Us",
    links: [
      { label: "VIP Club", to: "/vip-club" },
      { label: "Affiliate", to: "/affiliate" },
      { label: "Privacy Policy", to: "/privacy_policy" },
      { label: "Terms of Service", to: "/user_agreement" }
    ]
  },
  {
    title: "Payment Info",
    links: [
      { label: "Deposit Bonus Requirements", to: "/promotions" },
      { label: "Currency Guide", to: "/provably-fair" }
    ]
  },
  {
    title: "FAQ",
    links: [
      { label: "How-to Guides", to: "/provably-fair" },
      { label: "Online Casino Guide", to: "/provably-fair" },
      { label: "APP", to: "/winsmash.apk", external: true }
    ]
  }
];

function FLink({ link }) {
  if (link.external) {
    return (
      <FooterExternalLink href={link.to} download target="_blank" rel="noopener noreferrer">
        {link.label}
      </FooterExternalLink>
    );
  }
  return <FooterLink to={link.to}>{link.label}</FooterLink>;
}

const HelloFooter = () => {
  const [open, setOpen] = useState(null);
  const { data: groupsData } = useActiveGroups();
  const groups = groupsData || [];

  const { data: settingsData } = useSiteSettingsQuery();
  const settings = settingsData?.data?.[0] || {};

  let footerSections = [];
  if (settings.footer_sections) {
    try {
      footerSections = typeof settings.footer_sections === 'string'
        ? JSON.parse(settings.footer_sections)
        : settings.footer_sections;
    } catch (e) {
      console.error("Failed to parse footer sections:", e);
    }
  }

  const activeColsBase = (footerSections && footerSections.length > 0) ? footerSections : colsBase;

  const casinoGroupLinks = groups
    .filter(g => g.show_as_tab_casino)
    .map(g => ({
      label: groupNameTranslate(g.name),
      to: `/casino/group/${g.slug || g.id}`
    }));

  const sportsGroupLinks = groups
    .filter(g => g.show_as_tab_sports)
    .map(g => ({
      label: groupNameTranslate(g.name),
      to: `/sports/group/${g.slug || g.id}`
    }));

  function groupNameTranslate(name) {
    return name;
  }

  // Check if DB configuration already contains Casino or Sports columns to avoid duplicating
  const dbHasCasino = activeColsBase.some(c => c.title && c.title.toLowerCase() === 'casino');
  const dbHasSports = activeColsBase.some(c => c.title && c.title.toLowerCase() === 'sports');

  const defaultCasinoCol = {
    title: "Casino",
    links: [
      { label: "Casino Games", to: "/casino" },
      ...casinoGroupLinks,
      { label: "Providers", to: "/casino/providers" }
    ]
  };

  const defaultSportsCol = {
    title: "Sports",
    links: [
      { label: "Sportsbook", to: "/sports" },
      ...sportsGroupLinks
    ]
  };

  const dynamicCols = activeColsBase.map(c => {
    if (c.title && c.title.toLowerCase() === 'casino') {
      return {
        ...c,
        links: [
          { label: "Casino Games", to: "/casino" },
          ...casinoGroupLinks,
          { label: "Providers", to: "/casino/providers" }
        ]
      };
    }
    if (c.title && c.title.toLowerCase() === 'sports') {
      return {
        ...c,
        links: [
          { label: "Sportsbook", to: "/sports" },
          ...sportsGroupLinks
        ]
      };
    }
    return c;
  });

  // Prepend defaults only if they are not configured/positioned in the DB settings
  if (!dbHasCasino) {
    dynamicCols.unshift(defaultCasinoCol);
  }
  if (!dbHasSports) {
    // Insert Sports column right after Casino
    const casinoIdx = dynamicCols.findIndex(c => c.title && c.title.toLowerCase() === 'casino');
    dynamicCols.splice(casinoIdx + 1, 0, defaultSportsCol);
  }

  return (
    <StyledFooter>
      <Container>
        {/* Desktop Footer Grid */}
        <FooterGrid>
          {dynamicCols.map((c) => (
            <div key={c.title}>
              <ColumnTitle>{c.title}</ColumnTitle>
              <LinkList>
                {c.links.map((l) => (
                  <li key={l.label}>
                    <FLink link={l} />
                  </li>
                ))}
              </LinkList>
            </div>
          ))}
        </FooterGrid>

        {/* Mobile Accordions */}
        <MobileAccordionContainer>
          {dynamicCols.map((c) => (
            <AccordionItem key={c.title}>
              <AccordionTrigger onClick={() => setOpen(open === c.title ? null : c.title)}>
                <span>{c.title}</span>
                <ChevronDown
                  size={18}
                  style={{ transform: open === c.title ? "rotate(180deg)" : "rotate(0deg)" }}
                />
              </AccordionTrigger>
              {open === c.title && (
                <AccordionContent>
                  <LinkList>
                    {c.links.map((l) => (
                      <li key={l.label}>
                        <FLink link={l} />
                      </li>
                    ))}
                  </LinkList>
                </AccordionContent>
              )}
            </AccordionItem>
          ))}
        </MobileAccordionContainer>

        {/* Social Row */}
        <SocialRow>
          {settings.social_links && Array.isArray(settings.social_links) ? (
            settings.social_links
              .filter(s => s.is_enabled && s.show_in_footer)
              .map((s) => {
                if (s.icon_url) {
                  return (
                    <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer">
                      <img src={s.icon_url.startsWith('http') ? s.icon_url : `${API}${s.icon_url}`} style={{ width: "20px", height: "20px", objectFit: "contain", verticalAlign: "middle" }} alt={s.name} />
                    </a>
                  );
                }
                const IconComponent = LucideIcons[s.icon_name || 'Globe'] || LucideIcons.Globe;
                return (
                  <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                    <IconComponent size={20} />
                  </a>
                );
              })
          ) : (
            <>
              <LucideIcons.Newspaper size={20} />
              <LucideIcons.MessageSquare size={20} />
              <LucideIcons.Facebook size={20} />
              <LucideIcons.Twitter size={20} />
              <LucideIcons.Instagram size={20} />
              <LucideIcons.Youtube size={20} />
              <LucideIcons.ShoppingCart size={20} />
            </>
          )}
        </SocialRow>

        {/* Bottom Section */}
        <BottomSection>
          <p>{settings.footer_copyright || `© 2026 ${BRAND} | All Rights Reserved.`}</p>
          <p className="max-w-3xl mx-auto leading-relaxed">
            {settings.footer_description || `Twocent Technology Limited ke dwara operated, jo Belize me company registration number 000041939 ke sath registered hai, jiska registered address Sea Urchin Street, San Pedro, Ambergris Caye, Belize hai. ${BRAND_LINK} Autonomous Island of Anjouan, Union of Comoros ke dwara licensed aur regulated hai aur License No. ALSI-202410011-FI1 ke tahat operate hota hai. ${BRAND_LINK} ne sabhi regulatory compliance pass kar liye hain aur gaming operations ke liye legally authorized hai.`}
          </p>
          <LanguageButton>
            English <ChevronDown size={14} />
          </LanguageButton>
        </BottomSection>
      </Container>
    </StyledFooter>
  );
};

export default HelloFooter;
